import React, { useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import { useSnippetsActions } from '../store/snippetsStore.ts';
import type { Snippet } from '../types/snippet.ts';

import { messages } from './DeleteSnippetModal.i18n.ts';

type DeleteSnippetModalProps = DialogsContainerProps &
	WithFlagsProps & {
		snippet: Snippet;
		editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	};

const DeleteSnippetModalWithFlags = ({
	snippet,
	onClose,
	onCloseComplete,
	flags,
	editorAPI,
}: DeleteSnippetModalProps) => {
	const { Modal } = useChoreographerAssets();
	const titleId = useId();
	const intl = useIntl();
	const { fireModalClosedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.DELETE_SNIPPET_MODAL,
	);
	const snippetsActions = useSnippetsActions();
	const [isDeleting, setIsDeleting] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const handleCancel = () => {
		fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.DELETE_SNIPPET, {
			snippetId: snippet.snippetId,
			outcome: MODAL_OUTCOME.CANCELLED,
		});
		onClose();
	};

	const handleConfirm = async () => {
		setIsDeleting(true);
		setError(null);
		try {
			await snippetsActions.deleteSnippet(snippet.snippetId, {
				flags,
				intl,
				editorAPI: editorAPI ?? null,
			});
			fireModalClosedAnalytic(ANALYTICS_UI_SUBJECT_ID.DELETE_SNIPPET, {
				snippetId: snippet.snippetId,
				outcome: MODAL_OUTCOME.DELETED,
			});
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err : new Error(String(err)));
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_DELETE, {
				snippetId: snippet.snippetId,
			});
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<Modal onClose={handleCancel} onCloseComplete={onCloseComplete}>
			<ModalHeader>
				<Text id={titleId} weight="semibold" size="large">
					<FormattedMessage {...messages.deleteTemplateModalTitle} />
				</Text>
				<CloseButton onClick={handleCancel} />
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<Text>
						<FormattedMessage {...messages.deleteTemplateModalBody} />
					</Text>
					{error && (
						<Text color="color.text.danger" size="small">
							{error.message}
						</Text>
					)}
				</Stack>
			</ModalBody>
			<ModalFooter>
				<ButtonGroup>
					<Button appearance="subtle" onClick={handleCancel} isDisabled={isDeleting}>
						<FormattedMessage {...messages.deleteTemplateModalCancelButton} />
					</Button>
					<Button
						appearance="danger"
						onClick={handleConfirm}
						isLoading={isDeleting}
						isDisabled={isDeleting}
					>
						<FormattedMessage {...messages.deleteTemplateModalConfirmButton} />
					</Button>
				</ButtonGroup>
			</ModalFooter>
		</Modal>
	);
};

export const DeleteSnippetModal: React.ComponentType<
	Omit<
		DialogsContainerProps & {
			snippet: Snippet;
			editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
		},
		'flags'
	>
> = withFlags(DeleteSnippetModalWithFlags);
